/*
 * Schedule-time helpers shared by both V2 trees.
 *
 * The wire shape is unchanged from V1 and the batch-flow contracts keep it
 * (contracts README, "Schedule rows"; R-04-04): `timings` is an array of rows
 * that carry ALL SEVEN day keys, each `{ from, to }` in 24-hour IST "HH:mm",
 * unused days `{ from: "", to: "" }`, and a second row only when a weekday
 * repeats. The screen collects one start time per class as a "hh:mm AM" display
 * string plus the class length, so `to` is DERIVED here.
 */

import moment from "moment-timezone";

/* The backend judges "today" and every date it stores on IST wall time, so the
   screen has to ask the same clock rather than the browser's. */
const IST = "Asia/Kolkata";

/** Today as the backend sees it, "YYYY-MM-DD". */
export const istToday = () => moment().tz(IST).format("YYYY-MM-DD");

/**
 * An ISO instant → the IST calendar date it falls on. Every other date on the
 * wire is already an IST date; this is for the few that are instants.
 * @param {string} isoInstant
 * @returns {string} "YYYY-MM-DD", or "" when there is nothing to convert
 */
export const utcToIstDate = (isoInstant) =>
  isoInstant ? moment(isoInstant).tz(IST).format("YYYY-MM-DD") : "";

/** Day keys in the order the payload writes them. */
export const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

const EMPTY_SLOT = { from: "", to: "" };

/**
 * "04:30 PM" → 990 (minutes since midnight). Returns null for anything unparseable,
 * which is what an unset day looks like before the user picks a time.
 */
export const toMinutes = (displayTime) => {
  const match = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(displayTime?.trim?.() ?? "");
  if (!match) return null;

  const hour12 = Number(match?.[1]);
  const minute = Number(match?.[2]);
  if (hour12 < 1 || hour12 > 12 || minute > 59) return null;

  const isPm = match?.[3]?.toUpperCase() === "PM";
  /* 12 AM is midnight (0) and 12 PM is noon (12) — the one case a naive
     `hour12 + 12` gets wrong in both directions. */
  const hour24 = (hour12 % 12) + (isPm ? 12 : 0);
  return hour24 * 60 + minute;
};

/** 990 → "16:30". Wraps past midnight so a late slot cannot produce "24:30". */
export const toClock24 = (minutes) => {
  if (minutes === null || minutes === undefined) return "";
  const wrapped = ((minutes % 1440) + 1440) % 1440;
  const hours = String(Math.floor(wrapped / 60)).padStart(2, "0");
  return `${hours}:${String(wrapped % 60).padStart(2, "0")}`;
};

/**
 * The inverse of `toMinutes`: "16:30" → "04:30 PM". Edit loads a saved schedule in
 * the wire's 24-hour format but the picker edits display times, so without this the
 * round trip (load → edit → save) silently loses every time.
 * @param {string} clock24 - "HH:mm"
 * @returns {string} "hh:mm AM", or "" when there is nothing to convert
 */
export const toDisplayTime = (clock24) => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(clock24?.trim?.() ?? "");
  if (!match) return "";

  const hours = Number(match?.[1]);
  const minutes = Number(match?.[2]);
  if (hours > 23 || minutes > 59) return "";

  const meridiem = hours >= 12 ? "PM" : "AM";
  /* Midnight and noon are both written 12, not 0. */
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${String(hour12).padStart(2, "0")}:${match?.[2]} ${meridiem}`;
};

/**
 * The end time V1 used to collect by hand: start + class duration.
 * @param {string} displayTime - "04:30 PM"
 * @param {number|string} durationMinutes - the selected class duration
 * @returns {string} "HH:mm", or "" when either input is missing
 */
export const deriveEndTime = (displayTime, durationMinutes) => {
  const start = toMinutes(displayTime);
  const minutes = Number(durationMinutes);
  if (start === null || !minutes) return "";
  return toClock24(start + minutes);
};

/**
 * Build V1's `timings` array from the V2 schedule model.
 *
 * ROW PACKING MATTERS. V1 puts every *distinct* weekday into a SINGLE row — its
 * add-day handler drops the day into the first object that does not already have
 * it, and only starts a new object when the day is present in all of them
 * (AddBatchChild.jsx:288-320). So a Mon+Wed schedule is ONE row with both days
 * filled; a second row means "this weekday twice in a week", not "another day".
 * Emitting one row per day would describe a different schedule to the backend.
 *
 * @param {{day: string, time: string}[]} scheduleDays - e.g. [{ day: "mon", time: "04:30 PM" }]
 * @param {number|string} durationMinutes
 * @returns {Object[]} all-seven-keys rows, packed the way V1 packs them
 */
export const toTimingsPayload = (scheduleDays, durationMinutes) => {
  const packed = [];

  (scheduleDays ?? []).forEach((entry) => {
    const day = entry?.day;
    if (!DAY_KEYS?.includes(day)) return;

    let row = packed?.find((candidate) => !(day in candidate));
    if (!row) {
      row = {};
      packed.push(row);
    }

    row[day] = {
      from: toClock24(toMinutes(entry?.time)),
      to: deriveEndTime(entry?.time, durationMinutes),
    };
  });

  return packed?.map((row) =>
    DAY_KEYS?.reduce((filled, key) => ({ ...filled, [key]: row?.[key] ?? { ...EMPTY_SLOT } }), {}),
  );
};

/**
 * Saved `timings` rows → the flat, id-keyed class list the schedule picker edits.
 * The reverse of `toTimingsPayload`: each row carries all seven keys and only the
 * ones with a `from` are classes; a weekday in two rows becomes two entries.
 * @param {Object[]} timings
 * @returns {{id: string, day: string, time: string}[]}
 */
export const timingsToDays = (timings) =>
  (timings ?? []).flatMap((row, rowIndex) =>
    DAY_KEYS.filter((day) => row?.[day]?.from).map((day) => ({
      id: `${day}-${rowIndex}`,
      day,
      time: toDisplayTime(row?.[day]?.from),
    })),
  );

/**
 * "2026-10-05" → "05-10-2026", the design's date format; "" when there is none.
 * @param {string} isoDate
 * @returns {string}
 */
export const toDisplayDate = (isoDate) => {
  /* Strict: a shape the backend never promised should read as nothing, not as
     the literal "Invalid date" in the middle of a sentence. */
  const parsed = moment(isoDate ?? "", "YYYY-MM-DD", true);
  return parsed.isValid() ? parsed.format("DD-MM-YYYY") : "";
};

/**
 * How many classes a weekly schedule holds in a window — the
 * count a flex course's validity gives (E2 `schedule.sessionCount` for flex).
 * @param {Object} input
 * @param {string} input.startDate - "YYYY-MM-DD"
 * @param {string} input.endDate - "YYYY-MM-DD"
 * @param {{day: string}[]} input.days - one entry per weekly class
 * @returns {number}
 */
export const countClassesBetween = ({ startDate, endDate, days }) => {
  const start = moment(startDate, "YYYY-MM-DD", true);
  const end = moment(endDate, "YYYY-MM-DD", true);
  if (!start.isValid() || !end.isValid() || end.isBefore(start)) return 0;

  const perWeekday = (days ?? []).reduce(
    (counts, entry) => ({ ...counts, [entry?.day]: (counts?.[entry?.day] ?? 0) + 1 }),
    {},
  );
  let total = 0;
  /* The window's end date itself never carries a class — countFlexSessionsInWindow. */
  for (const cursor = start.clone(); cursor.isBefore(end); cursor.add(1, "day")) {
    total += perWeekday?.[DAY_KEYS[(cursor.isoWeekday() + 6) % 7]] ?? 0;
  }
  return total;
};

/**
 * Whether a start date falls on one of the schedule's weekdays — the backend
 * refuses one that does not (`start-date-not-class-day`, R-04-05).
 * @param {string} startDate - "YYYY-MM-DD"
 * @param {{day: string}[]} days
 * @returns {boolean}
 */
export const isClassDay = (startDate, days) => {
  const date = moment(startDate, "YYYY-MM-DD", true);
  if (!date.isValid()) return false;
  const weekday = DAY_KEYS[(date.isoWeekday() + 6) % 7];
  return (days ?? []).some((entry) => entry?.day === weekday);
};

/**
 * The first class day of a schedule strictly after a date — where a successor
 * starts after its predecessor's last class (R-07-05).
 * @param {string} afterDate - "YYYY-MM-DD"
 * @param {{day: string}[]} days
 * @returns {string} "YYYY-MM-DD", or "" when either input is missing
 */
export const firstClassDayAfter = (afterDate, days) => {
  const start = moment(afterDate, "YYYY-MM-DD", true);
  if (!start.isValid() || !(days ?? []).length) return "";
  const cursor = start.clone().add(1, "day");
  for (let step = 0; step < 7; step += 1, cursor.add(1, "day")) {
    if (isClassDay(cursor.format("YYYY-MM-DD"), days)) return cursor.format("YYYY-MM-DD");
  }
  return "";
};

/**
 * Classes added to the schedule but never given a start time.
 * @param {{id: string, time: string|null}[]} days
 * @returns {string[]} the ids still missing a time
 */
export const daysMissingTime = (days) =>
  (days ?? [])?.filter((entry) => !entry?.time)?.map((entry) => entry?.id);

/**
 * Two classes on the same weekday at the same time — both screens edit the same
 * picker, so both gate on it.
 * @param {{day: string, time: string|null}[]} days
 * @returns {boolean}
 */
export const hasDuplicateClass = (days) => {
  const seen = (days ?? [])
    ?.filter((entry) => entry?.time)
    ?.map((entry) => `${entry?.day} ${entry?.time}`);
  return new Set(seen)?.size !== seen?.length;
};

const MINUTES_IN_WEEK = 7 * 1440;

/* A class's start on the weekly clock, Monday 00:00 = 0. */
const weekMinute = (day, time) => {
  const minutes = toMinutes(time);
  const dayIndex = DAY_KEYS?.indexOf(day);
  return minutes === null || dayIndex < 0 ? null : dayIndex * 1440 + minutes;
};

/**
 * Whether a class at `day time` would run into — or be run into by — any of
 * `others`, the backend's `timings-overlap` rule (Q-D1): each class runs for its
 * length on the weekly clock, so a late class meets the next day's first and
 * Sunday's last meets Monday's first. The same start twice is a clash too.
 * @param {{day: string, time: string|null}} entry
 * @param {{day: string, time: string|null}[]} others
 * @param {number|string} durationMinutes
 * @returns {boolean}
 */
export const clashesWithOthers = (entry, others, durationMinutes) => {
  const length = Number(durationMinutes) || 60;
  const start = weekMinute(entry?.day, entry?.time);
  if (start === null) return false;
  return (others ?? [])?.some((other) => {
    const otherStart = weekMinute(other?.day, other?.time);
    if (otherStart === null) return false;
    const gap = (((otherStart - start) % MINUTES_IN_WEEK) + MINUTES_IN_WEEK) % MINUTES_IN_WEEK;
    return gap < length || (MINUTES_IN_WEEK - gap) % MINUTES_IN_WEEK < length;
  });
};

/**
 * Two classes that run into each other, as the backend refuses them
 * (`timings-overlap`): on one weekday, or across midnight into the next.
 * @param {{day: string, time: string|null}[]} days
 * @param {number|string} durationMinutes
 * @returns {boolean}
 */
export const hasOverlappingClass = (days, durationMinutes) =>
  (days ?? [])?.some((entry, index) =>
    clashesWithOthers(entry, (days ?? [])?.slice(0, index), durationMinutes),
  );
